import React, { useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';

/**
 * Accessible, theme-aware replacement for window.confirm().
 *
 * window.confirm is modal-blocking (freezes the JS loop), can't carry
 * additional context (we can't show "this will disable 5 active
 * triggers"), doesn't honor dark mode, and traps focus poorly on
 * screen readers. This component offers the same "are you sure?"
 * gate but with richer presentation and a consistent UX across the
 * app.
 *
 * Usage (imperative via the hook):
 *
 *   const { confirm, dialog } = useConfirm();
 *   ...
 *   const ok = await confirm({
 *     title: 'Delete trigger?',
 *     message: 'This permanently removes the trigger and its history.',
 *     confirmLabel: 'Delete',
 *     severity: 'error',
 *   });
 *   if (ok) { ... }
 *
 *   // In JSX: {dialog}
 */
export function ConfirmDialog({
  open,
  title = 'Confirm',
  message = '',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  severity = 'primary', // 'primary' | 'error' | 'warning'
  onConfirm,
  onCancel,
}) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-dialog-description">
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} autoFocus>
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          color={severity === 'error' ? 'error' : severity === 'warning' ? 'warning' : 'primary'}
          variant="contained"
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Imperative hook — returns a `confirm(options)` function that
 * resolves to true/false, plus a `dialog` React node that must be
 * rendered in the component.
 */
export function useConfirm() {
  const [state, setState] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    severity: 'primary',
    resolve: null,
  });

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title: options.title || 'Confirm',
        message: options.message || '',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        severity: options.severity || 'primary',
        resolve,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    state.resolve && state.resolve(true);
    setState((prev) => ({ ...prev, open: false, resolve: null }));
  }, [state]);

  const handleCancel = useCallback(() => {
    state.resolve && state.resolve(false);
    setState((prev) => ({ ...prev, open: false, resolve: null }));
  }, [state]);

  const dialog = (
    <ConfirmDialog
      open={state.open}
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      cancelLabel={state.cancelLabel}
      severity={state.severity}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { confirm, dialog };
}

export default ConfirmDialog;
